import { AsButton, AsLabel, AsText, Spacer, useModal, getClaimValue } from '@adserve/adserve-react-components';
import React, { useState, useEffect } from 'react';
import { AttachmentType } from '../../../../constants/Enums';
import { deleteAttachment, updateAttachment, upload } from '../../../../services/AttachmentService';
import { createNote, updateNote } from '../../../../services/NoteService';
import RichTextEditor from '../../../generic/RichTextEditor/RichTextEditor';
import { inputStyle, labelStyle } from '../CampaignBriefStyles';
import CampaignAttachmentCreateButton from './CampaignAttachmentCreateButton';
import DragDropRegion from './DragDropRegion';

// ASSP-8023 Lighter font used elsewhere - allows rich text bold to show clearly
//
export const fontStyle = {
  fontFamily: "Roboto, Arial, sans-serif",
  letterSpacing: 0,
  fontWeight: 400,
  color: '#414141',
};

const headingStyle = {
    ...fontStyle,
    fontWeight: 450,
};

const CampaignAttachmentCreate = ({
    attachmentTypeId,
    data = {},
    notes = [],
    isEditing = false,
    campaignId,
    changeMade,
    onChange
}) => {
    const { removeMessage, addMessage } = useModal();

    const [attachment, setAttachment] = useState(data);
    const [progress, setProgress] = useState(0);
    const [currentFile, setCurrentFile] = useState(undefined);

    useEffect(() => {
        if (data?.file !== undefined) {
            setProgress(100);
        }
    }, [data?.file]);

    const handleNameUpdate = (event) => {
        setAttachment({ ...attachment, name: event.target.value });
    };

    const handleDescriptionUpdate = (event) => {
        setAttachment({ ...attachment, description: event.target.value });
    };

    const handleNoteValueUpdate = (value) => {
        setAttachment({ ...attachment, value });
    };

    const handleClose = () => {
        removeMessage();
    };

    const onUploadProgress = (e) => {
        setProgress(Math.round((100 * e.loaded) / e.total));
    };

    const handleUpload = async () => {
        let success = true;
        const attachmentDescription = attachment.description ?? '';
        if (campaignId === window.emptyGuid || campaignId === undefined) {
            if (attachmentTypeId === AttachmentType.NOTE.id) {
                changeMade('campaignNotes', [
                    ...notes.filter((d) => d.attachmentTypeId === AttachmentType.NOTE.id),
                    {
                        id: attachment.id,
                        name: attachment.name,
                        description: attachmentDescription,
                        entityId: campaignId,
                        slateValue: JSON.stringify(attachment.value),
                        type: AttachmentType.NOTE.name,
                        attachmentTypeId: AttachmentType.NOTE.id,
                        value: attachment.value,
                        dateCreated: new Date(),
                        attachmentOwner: getClaimValue('fullName')
                    }
                ]);
            } else {
                changeMade('campaignAttachments', [
                    ...notes.filter((d) => d.attachmentTypeId === AttachmentType.ATTACHMENT.id),
                    {
                        id: attachment.id,
                        name: attachment.name,
                        description: attachmentDescription,
                        file: currentFile,
                        type: AttachmentType.ATTACHMENT.name,
                        attachmentTypeId: AttachmentType.ATTACHMENT.id,
                        dateCreated: new Date(),
                        attachmentOwner: getClaimValue('fullName')
                    }
                ]);
                setProgress(100);
            }

            handleClose();
            return;
        }

        if (attachmentTypeId === AttachmentType.NOTE.id) {
            // Note upload
            const payload = {
                id: attachment.id,
                name: attachment.name,
                description: attachmentDescription,
                entityId: campaignId,
                slateValue: JSON.stringify(attachment.value)
            };
            if (isEditing) {
                await updateNote(payload, 'campaign');
            } else {
                await createNote(payload, 'campaign');
            }
        } else {
            // Attachment upload
            setProgress(0);
            if (isEditing && currentFile === undefined) {
                try {
                    await updateAttachment(
                        {
                            id: attachment.id,
                            name: attachment.name,
                            description: attachmentDescription,
                            file: currentFile
                        },
                        'campaign'
                    );
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(err.message ?? err.response?.data);
                    addMessage({
                        message: window.translate(
                            `An error occurred while attempting to update this attachment. ${err?.response?.data}`
                        ),
                        buttons: [
                            { text: window.translate('Ok') },
                            { text: window.translate('Retry'), callback: handleUpload, useSecondaryStyle: true }
                        ]
                    });
                    success = false;
                }
            } else {
                try {
                    await upload(
                        currentFile,
                        onUploadProgress,
                        campaignId,
                        attachment.name,
                        attachmentDescription,
                        'campaign'
                    );
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(err.message ?? err.response?.data);
                    addMessage({
                        message: window.translate(
                            `An error occurred while attempting to upload this attachment. ${err?.response?.data}`
                        ),
                        buttons: [
                            { text: window.translate('Ok') },
                            { text: window.translate('Retry'), callback: handleUpload, useSecondaryStyle: true }
                        ]
                    });
                    success = false;
                }

                // If we're replacing a file
                if (isEditing) {
                    try {
                        await deleteAttachment(
                            attachment.id, // id
                            'campaign'
                        );
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error(err.message ?? err.response?.data);
                        addMessage({
                            message: window.translate(
                                `An error occurred while attempting to delete an attachment. ${err?.response?.data}`
                            ),
                            buttons: [
                                { text: window.translate('Ok') },
                                { text: window.translate('Retry'), callback: handleUpload, useSecondaryStyle: true }
                            ]
                        });
                        success = false;
                    }
                }
            }
        }

        if (success) {
            onChange();
            handleClose();
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h1 style={headingStyle}>
                    {isEditing
                        ? `Editing ${attachmentTypeId === AttachmentType.NOTE.id ? 'Note' : 'Attachment'}`
                        : 'Create New'}
                </h1>
                <AsLabel id='attachments-mandatory-fields-label'>{window.translate('* Mandatory fields')}</AsLabel>
            </div>
            <div style={{ display: 'flex' }}>
                <div style={{ width: '33%' }}>
                    <AsText
                        inputStyle={{ inputStyle, ...fontStyle }}
                        labelStyle={labelStyle}
                        title='Name *'
                        value={attachment?.name}
                        onChange={handleNameUpdate}
                        autoFocus
                    />
                </div>
                <Spacer width={47} />
                <div style={{ width: '65%' }}>
                    <AsText
                        inputStyle={{ inputStyle, ...fontStyle }}
                        labelStyle={labelStyle}
                        title='Description'
                        value={attachment?.description}
                        onChange={handleDescriptionUpdate}
                    />
                </div>
            </div>
            <Spacer height={49} />
            <div style={{ height: 200, ...fontStyle }}>
                {attachmentTypeId === AttachmentType.NOTE.id ? (
                    <RichTextEditor style={{ flex: 3 }} value={attachment?.value} updateValue={handleNoteValueUpdate} />
                ) : (
                    <DragDropRegion
                        style={{ flex: 3 }}
                        onFileSelect={setCurrentFile}
                        file={attachment?.file}
                        progress={progress}
                        disabled={!attachment?.name || attachment?.name?.length < 3}
                    />
                )}
            </div>
            <Spacer height={49} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20, ...fontStyle }}>
                <AsButton
                    style={{ height: 34, fontSize: 15, width: 'max-content', padding: '0 10px', minWidth: 84 }}
                    useTertiaryStyle
                    handleClick={handleClose}
                >
                    {window.translate('Cancel')}
                </AsButton>

                <CampaignAttachmentCreateButton
                    attachmentTypeId={attachmentTypeId}
                    currentFile={currentFile}
                    onClick={handleUpload}
                    attachment={attachment}
                    isEditing={isEditing}
                    progress={progress}
                />
            </div>
        </div>
    );
};

export default CampaignAttachmentCreate;
